import {
	faArrowTurnDownLeft,
	faChevronsRight,
	faRobot,
	faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";

import { Button } from "@/components/Button/Button";
import { Icon, PermissionCheck } from "@/components";
import { ERROR_TYPE, translateError } from "@/utils";
import DeleteModal from "@/components/Modals/DeleteModal";
import { ButtonColor } from "@/components/Button/types";
import { PermissionRoles } from "@/enum";

import {
	SubTaskInfo,
	TaskInfo,
	UpdateSubTaskInfo,
	UpdateSubTaskInfoSchema,
} from "../schema/tasks-and-subtasks";
import { ToDoStatusDropDown } from "../../ToDo/ToDoList/components/ToDoStatusDropdown";
import ActivityTabs from "./ActivityTabs";
import DetailsMeta from "./DetailsMeta";
import UnsavedChangesModal from "./UnsavedChangesModal";
import AssigneeSelect from "./fields/AssigneeSelect";
import TagSelect from "./fields/TagSelect";
import { useUnsavedGuard } from "../useUnsavedGuard";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";

const AiChat = lazy(() => import("./AiChat"));

interface SubTaskDetailsProps {
	subTask: SubTaskInfo | null;
	parentTaskId: number;
	open: boolean;
	onClose: () => void;
	parentData: TaskInfo;
	/** Opens the parent task drawer — the breadcrumb's target. */
	onOpenParentTask?: (taskId: number) => void;
}

const railLabel = "text-[12px] font-bold uppercase tracking-wide leading-4 text-subtle";

export default function SubTaskDetails({
	subTask,
	parentTaskId,
	open,
	onClose,
	parentData,
	onOpenParentTask,
}: SubTaskDetailsProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const subTaskRef = useRef(subTask);
	const { updateSubTask, deleteSubTask } = useToDoSocket();

	useEffect(() => {
		subTaskRef.current = subTask;
	}, [subTask]);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = useForm<UpdateSubTaskInfo>({
		defaultValues: {
			id: subTask?.id ?? -1,
			taskId: parentTaskId,
			title: subTask?.title ?? "",
			description: subTask?.description ?? "",
			complexityEstimate: subTask?.complexityEstimate,
			status: subTask?.status,
			priority: subTask?.priority,
			tagId: subTask?.tagId ?? null,
			assigneeId: subTask?.assigneeId,
		},
		resolver: zodResolver(UpdateSubTaskInfoSchema),
	});

	useEffect(() => {
		if (subTask) {
			reset({
				id: subTask.id,
				taskId: parentTaskId,
				title: subTask.title,
				description: subTask.description ?? "",
				complexityEstimate: subTask.complexityEstimate,
				status: subTask.status,
				priority: subTask.priority,
				tagId: subTask.tagId ?? null,
				assigneeId: subTask.assigneeId,
			});
		}
	}, [
		subTask?.id,
		subTask?.assigneeId,
		subTask?.title,
		subTask?.status,
		subTask?.priority,
		subTask?.tagId,
	]);

	const onSubmit = useCallback(
		(formValues: UpdateSubTaskInfo) => {
			const current = subTaskRef.current;

			if (!current) {
				return;
			}

			const merged: UpdateSubTaskInfo = {
				...formValues,
				id: current.id ?? -1,
				taskId: parentTaskId,
				status: current.status ?? formValues.status,
				description: formValues.description || null,
				tagId: formValues.tagId ?? null,
			};

			updateSubTask(merged.taskId, merged.id ?? -1, merged);
			reset(merged);
		},
		[updateSubTask, reset, parentTaskId]
	);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isAiChatOpen, setIsAiChatOpen] = useState(false);

	const handleSave = useCallback(async () => {
		await handleSubmit(onSubmit)();
	}, [handleSubmit, onSubmit]);

	const { guard, isPrompting, keepEditing, discard, saveAndContinue } = useUnsavedGuard({
		isDirty,
		reset,
		save: handleSave,
	});

	/** Every route out of the drawer goes through the guard, so no edit is dropped silently. */
	const requestClose = useCallback(() => guard(() => onClose()), [guard, onClose]);

	const goToParent = useCallback(
		() => guard(() => onOpenParentTask?.(parentTaskId)),
		[guard, onOpenParentTask, parentTaskId]
	);

	useEffect(() => {
		if (!open || !subTask?.id) {
			return;
		}

		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Escape") {
				return;
			}

			if (e.defaultPrevented) {
				return;
			}

			if (isDeleteModalOpen || isAiChatOpen || isPrompting) {
				return;
			}

			requestClose();
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [open, subTask?.id, isDeleteModalOpen, isAiChatOpen, isPrompting, requestClose]);

	return (
		<>
			<div
				aria-hidden
				className={`fixed inset-0 z-[55] bg-black/30 transition-opacity duration-300 ease-in-out
					${open && subTask?.id ? "opacity-100" : "opacity-0 pointer-events-none"}`}
				onClick={requestClose}
			/>
			<div
				className={`fixed right-0 top-0 z-[60] flex h-[100vh] w-[min(880px,96vw)] flex-col bg-white shadow-lg overflow-y-auto
					transition-all duration-300 ease-in-out
					${
						open && subTask?.id
							? "translate-x-0 opacity-100"
							: "translate-x-full opacity-0 pointer-events-none"
					}`}
			>
				{!!subTask?.id && (
					<>
						<div className="sticky top-0 z-10 w-full bg-white px-6 py-4 border-b border-border flex items-center justify-between gap-4">
							<div className="flex items-center gap-2 min-w-0">
								<Button
									className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !shadow-none hover:bg-primary-gray-lightest"
									image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faChevronsRight} />}
									title=""
									onClick={requestClose}
								/>
								{/* Opening a subtask replaces the task drawer; this is the way back. */}
								{onOpenParentTask && parentData?.title ? (
									<button
										className="flex items-center gap-2 min-w-0 rounded-[12px] px-2 py-1 text-left transition-colors hover:bg-primary-gray-lightest"
										title={parentData.title}
										type="button"
										onClick={goToParent}
									>
										<Icon className="w-3 h-3 shrink-0 text-subtle" icon={faArrowTurnDownLeft} />
										<span className="text-[12px] leading-4 text-subtle shrink-0">
											{ts("backToTask")}
										</span>
										<span className="truncate text-[13px] font-semibold leading-5 text-primary-blue-dark">
											{parentData.title}
										</span>
									</button>
								) : null}
							</div>
							<div className="flex items-center gap-3">
								{isDirty ? (
									<span className="text-[12px] leading-4 text-accent-strong whitespace-nowrap">
										{ts("unsaved.badge")}
									</span>
								) : null}
								<Button
									className="rounded-[12px] bg-white !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4 !border-[0.28px] !border-border hover:bg-primary-gray-lightest"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="text-accent-strong w-5 h-5 mr-2" icon={faRobot} />}
									title={t("aiChat.toggle", "AI Assistant")}
									onClick={() => setIsAiChatOpen((v) => !v)}
								/>
								<Button
									className="rounded-[12px] !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4"
									color={ButtonColor.ACTION}
									disabled={!isDirty}
									title={t("createTask.save")}
									onClick={handleSave}
								/>
							</div>
						</div>

						{/* grow/shrink-0: fills the drawer when the subtask is short so the rail
						    reaches the bottom, and still grows past it when it is long. */}
						<div className="grid grow shrink-0 basis-auto grid-cols-1 sm:grid-cols-[minmax(0,1fr)_300px] items-stretch">
							<div className="p-6 min-w-0 flex flex-col gap-6">
								<div className="w-full flex flex-col gap-2">
									<p className={railLabel}>{t("createTask.title")}</p>
									<Controller
										control={control}
										name="title"
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												error={!!errors?.title}
												helperText={
													!!errors?.title?.message
														? translateError[errors?.title?.message as unknown as ERROR_TYPE]
														: errors?.title?.message
												}
												inputProps={{
													style: { padding: "8px", fontSize: "18px", fontWeight: 600 },
												}}
												size="small"
											/>
										)}
									/>
								</div>

								<div className="w-full flex flex-col gap-2">
									<p className={railLabel}>{t("createTask.description")}</p>
									<Controller
										control={control}
										name="description"
										render={({ field }) => (
											<TextField
												{...field}
												fullWidth
												multiline
												maxRows={10}
												minRows={5}
												size="small"
											/>
										)}
									/>
								</div>

								<ActivityTabs companyToDoProgressId={subTask.id} isTask={false} />
							</div>

							<aside className="p-6 flex flex-col gap-6 border-t sm:border-t-0 sm:border-l border-border bg-neutral-25">
								<ToDoStatusDropDown companyToDoProgressId={subTask.id} isTask={subTask?.isTask} />

								<div className="w-full flex flex-col gap-1.5">
									<p className={railLabel}>{t("createTask.assignee")}</p>
									<Controller
										control={control}
										name="assigneeId"
										render={({ field }) => (
											<AssigneeSelect value={field.value} onChange={field.onChange} />
										)}
									/>
								</div>

								<div className="w-full flex flex-col gap-1.5">
									<p className={railLabel}>{t("createTask.tags")}</p>
									<Controller
										control={control}
										name="tagId"
										render={({ field }) => (
											<TagSelect value={field.value} onChange={field.onChange} />
										)}
									/>
								</div>

								<DetailsMeta createdAt={subTask.createdAt} updatedAt={subTask.updatedAt} />

								<PermissionCheck requiredPermissions={[PermissionRoles.ADMIN]}>
									<div className="mt-auto pt-4 border-t border-border">
										<Button
											className="rounded-[12px] w-full !min-h-[44px] !py-2 !px-4"
											color={ButtonColor.DANGER_SECONDARY}
											image={<Icon className="w-5 h-5 mr-2" icon={faTrashCan} />}
											title={t("createTask.deleteSubtask")}
											onClick={() => {
												if (subTask?.id) {
													setIsDeleteModalOpen(true);
												}
											}}
										/>
									</div>
								</PermissionCheck>
							</aside>
						</div>

						<Suspense fallback={null}>
							<AiChat
								isOpen={isAiChatOpen}
								subtaskId={subTask.id}
								taskId={parentTaskId}
								onClose={() => setIsAiChatOpen(false)}
							/>
						</Suspense>
					</>
				)}
			</div>

			<DeleteModal
				description={t("manage-to-dos.delete-description", { taskTitle: subTask?.title })}
				handleClose={() => setIsDeleteModalOpen(false)}
				handleDelete={() => {
					if (subTask?.id) {
						deleteSubTask(parentTaskId, subTask.id);
						setIsDeleteModalOpen(false);
						onClose();
					}
				}}
				isShowing={isDeleteModalOpen}
				title={t("manage-to-dos.delete-title")}
			/>
			<UnsavedChangesModal
				isOpen={isPrompting}
				onDiscard={discard}
				onKeepEditing={keepEditing}
				onSave={saveAndContinue}
			/>
		</>
	);
}
